import { useEffect, useState } from 'react';

import { tamanhoDoArquivo } from '@maestra/core/nucleo/cotaDeArmazenamento';
import { useArquivoDaConversa } from '@maestra/core/hooks/useArquivoDaConversa';
import { blobDoArquivo, linkParaBaixar, type ArquivoDoPerfil } from '@maestra/core/services/db/arquivos';
import { useLocalPlayerStore } from '@maestra/core/stores/localPlayerStore';

// O ARQUIVO QUE A NYTA CITOU (`[[arquivo:<caminho>]]`, ver `nucleo/arquivosNaFala`), sem tela.
// Copiado de `components/nyta/ArquivoNaConversa.tsx`: o cartão lê com a sessão de quem está na
// conversa (mostra só o que essa pessoa já veria em Arquivos); o áudio toca no PLAYER DO SISTEMA,
// e não num `<audio>` do navegador (duas fontes de verdade); o PDF abre num endereço LOCAL (blob),
// porque o link assinado do Storage manda baixar em vez de mostrar.

const ROTULO: Record<ArquivoDoPerfil['categoria'], string> = {
  audio: 'Áudio', guia: 'Guia', pista: 'Pista', capa: 'Capa', imagem: 'Imagem', video: 'Vídeo', documento: 'Documento', outro: 'Arquivo',
};

const ehAudio = (a: ArquivoDoPerfil) => a.categoria === 'audio' || a.categoria === 'guia' || a.categoria === 'pista';
const ehImagem = (a: ArquivoDoPerfil) => a.categoria === 'imagem' || a.categoria === 'capa';
const ehPdf = (a: ArquivoDoPerfil) => /\.pdf$/i.test(a.nome) || a.mime === 'application/pdf';

/** O tempo do áudio no cartão ("1:05"). */
export const tempoDoAudio = (s: number) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
const tempo = tempoDoAudio;

/** A linha de apoio do cartão ("Guia · 4,1 MB · Varanda"): a conversa de exemplo usa a mesma. */
export const apoioDoArquivo = (a: Pick<ArquivoDoPerfil, 'categoria' | 'bytes' | 'musica'>) => (
  `${ROTULO[a.categoria]} · ${tamanhoDoArquivo(a.bytes)}${a.musica ? ` · ${a.musica}` : ''}`
);

export const useArquivoNaConversa = (artistId: string | null | undefined, caminho: string) => {
  const estado = useArquivoDaConversa(artistId, caminho);
  const [vendo, setVendo] = useState(false);
  const [pdfLocal, setPdfLocal] = useState<string | null>(null);
  useEffect(() => () => { if (pdfLocal) URL.revokeObjectURL(pdfLocal); }, [pdfLocal]);

  const atual = useLocalPlayerStore((st) => st.currentId === caminho);
  const tocando = useLocalPlayerStore((st) => st.currentId === caminho && st.playing);
  const time = useLocalPlayerStore((st) => (st.currentId === caminho ? st.time : 0));
  const duration = useLocalPlayerStore((st) => (st.currentId === caminho ? st.duration : 0));
  const toggle = useLocalPlayerStore((st) => st.toggle);
  const setTracks = useLocalPlayerStore((st) => st.setTracks);
  const setCurrentId = useLocalPlayerStore((st) => st.setCurrentId);
  const setOpen = useLocalPlayerStore((st) => st.setOpen);

  if (estado.estado === 'carregando') return { estado: 'carregando' } as const;
  if (estado.estado === 'sumiu') return { estado: 'sumiu' } as const;

  const { arquivo, link, trecho } = estado;
  const apoio = apoioDoArquivo(arquivo);
  const baixar = async () => { window.open(await linkParaBaixar(arquivo), '_blank', 'noopener'); };

  return {
    estado: 'pronto' as const,
    nome: arquivo.nome,
    apoio,
    link,
    trecho: trecho ? `${trecho}${trecho.length >= 600 ? '…' : ''}` : null,
    tipo: ehImagem(arquivo) ? 'imagem' as const : arquivo.categoria === 'video' ? 'video' as const : ehAudio(arquivo) ? 'audio' as const : ehPdf(arquivo) ? 'pdf' as const : 'outro' as const,
    baixar,
    visualizador: (ehImagem(arquivo) || ehPdf(arquivo)) ? {
      itens: [{ id: arquivo.caminho, tipo: ehPdf(arquivo) ? 'pdf' as const : 'imagem' as const, url: ehPdf(arquivo) ? pdfLocal : link, nome: arquivo.nome, apoio }],
      aberto: vendo ? arquivo.caminho : null,
      abrir: async () => {
        setVendo(true);
        if (!ehPdf(arquivo) || pdfLocal) return;
        try {
          setPdfLocal(URL.createObjectURL(new Blob([await blobDoArquivo(arquivo)], { type: 'application/pdf' })));
        } catch {
          setVendo(false);
        }
      },
      fechar: () => setVendo(false),
    } : null,
    tocador: ehAudio(arquivo) && link ? {
      tocando,
      andamento: duration > 0 ? Math.min(100, (time / duration) * 100) : 0,
      tempo: atual ? `${tempo(time)} de ${tempo(duration)}` : null,
      alternar: () => {
        if (atual) { toggle?.(); return; }
        setTracks([{
          id: arquivo.caminho,
          title: arquivo.nome,
          subtitle: [arquivo.musica, ROTULO[arquivo.categoria]].filter(Boolean).join(' · '),
          url: link,
          href: artistId ? `/artists/${artistId}/arquivos` : undefined,
          onde: 'Arquivos',
        }]);
        setCurrentId(arquivo.caminho);
        setOpen(true);
      },
    } : null,
  };
};
